import { useState } from 'react'
import { api, useAbruf, type ApiFehler } from '../api'
import { Angabe, Fehleranzeige, Karte, Merkmal, Tabelle, Zustand } from '../bausteine'
import { datum, prozent, zahl } from '../formate'

interface Anteil {
  energietraeger: string
  bezeichnung: string
  prozent: string
  erneuerbar: boolean
}

interface Kennzeichnungsblatt {
  jahr: number
  bezeichnung: string | null
  anteile: Anteil[]
  anteilErneuerbar: string
  co2GrammProKwh: string
  atommuellMilligrammProKwh: string
  veroeffentlichtAm: string | null
  beanstandungen: string[]
  belegtext: string | null
}

interface Bestandsblatt {
  verfuegbarMwh: string
}

export default function Stromkennzeichnung() {
  const kennzeichnung = useAbruf<Kennzeichnungsblatt>('/api/stromkennzeichnung')
  const bestand = useAbruf<Bestandsblatt>('/api/stromkennzeichnung/herkunftsnachweise')

  const [meldung, setzeMeldung] = useState<string>()
  const [fehler, setzeFehler] = useState<ApiFehler>()

  async function abgleichen() {
    setzeFehler(undefined)
    setzeMeldung(undefined)
    try {
      const ergebnis = await api.sende<{ neuAufgenommen: number; verfuegbarMwh: string }>(
        '/api/stromkennzeichnung/herkunftsnachweise/abgleich',
        {},
      )
      setzeMeldung(
        `${ergebnis.neuAufgenommen} Nachweise neu aufgenommen, verfügbar sind nun ${zahl(
          ergebnis.verfuegbarMwh,
        )} MWh.`,
      )
      bestand.erneut()
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler)
    }
  }

  return (
    <>
      <h1>Stromkennzeichnung</h1>
      <p className="untertitel">
        Nach Paragraf 42 EnWG sind Energieträgermix, Kohlendioxidemissionen und
        radioaktiver Abfall auszuweisen - im Vergleich zum deutschen Durchschnitt.
      </p>

      <Karte titel="Kennzeichnung des Bezugsjahres">
        <Zustand laeuft={kennzeichnung.laeuft} fehler={kennzeichnung.fehler}>
          {kennzeichnung.daten && (
            <>
              {kennzeichnung.daten.beanstandungen.length > 0 && (
                <div className="hinweis fehler">
                  <strong>Die Kennzeichnung ist nicht schlüssig:</strong>
                  <ul>
                    {kennzeichnung.daten.beanstandungen.map((beanstandung) => (
                      <li key={beanstandung}>{beanstandung}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="angaben" style={{ marginBottom: 16 }}>
                <Angabe beschriftung="Bezugsjahr">{kennzeichnung.daten.jahr}</Angabe>
                <Angabe beschriftung="Bezeichnung">
                  {kennzeichnung.daten.bezeichnung ?? 'Gesamtstromlieferung'}
                </Angabe>
                <Angabe beschriftung="Erneuerbar">
                  {prozent(kennzeichnung.daten.anteilErneuerbar)}
                </Angabe>
                <Angabe beschriftung="Kohlendioxid">
                  {zahl(kennzeichnung.daten.co2GrammProKwh, 0)} g/kWh
                </Angabe>
                <Angabe beschriftung="Radioaktiver Abfall">
                  {zahl(kennzeichnung.daten.atommuellMilligrammProKwh, 5)} mg/kWh
                </Angabe>
                <Angabe beschriftung="Veröffentlicht am">
                  {datum(kennzeichnung.daten.veroeffentlichtAm)}
                </Angabe>
              </div>

              <Tabelle
                spalten={[
                  { name: 'Energieträger' },
                  { name: 'Art' },
                  { name: 'Anteil', zahl: true },
                ]}
              >
                {kennzeichnung.daten.anteile.map((anteil) => (
                  <tr key={anteil.energietraeger}>
                    <td>{anteil.bezeichnung}</td>
                    <td>
                      {anteil.erneuerbar ? (
                        <Merkmal art="gut">Erneuerbar</Merkmal>
                      ) : (
                        <Merkmal>Konventionell</Merkmal>
                      )}
                    </td>
                    <td className="zahl">{prozent(anteil.prozent)}</td>
                  </tr>
                ))}
              </Tabelle>

              {kennzeichnung.daten.belegtext && (
                <div className="hinweis info" style={{ marginTop: 16 }}>
                  <strong>Text auf dem Beleg</strong>
                  <div>{kennzeichnung.daten.belegtext}</div>
                </div>
              )}
            </>
          )}
        </Zustand>
      </Karte>

      <Karte
        titel="Herkunftsnachweise"
        handlung={<button onClick={abgleichen}>Mit Register abgleichen</button>}
      >
        {meldung && <div className="hinweis info">{meldung}</div>}
        {fehler && <Fehleranzeige fehler={fehler} />}
        <Zustand laeuft={bestand.laeuft} fehler={bestand.fehler}>
          {bestand.daten && (
            <div className="angaben">
              <Angabe beschriftung="Verfügbarer Bestand">
                {zahl(bestand.daten.verfuegbarMwh)} MWh
              </Angabe>
            </div>
          )}
        </Zustand>
        <div className="hinweis warnung" style={{ marginTop: 14, marginBottom: 0 }}>
          Ohne Anbindung an das Register des Umweltbundesamtes liefert eine Attrappe. Erst
          die tatsächliche Entwertung berechtigt dazu, Strom als erneuerbar auszuweisen.
        </div>
      </Karte>
    </>
  )
}
